import { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { addOutsidePress, removeOutsidePress } from "./outsidePress";

// Categorized emoji dataset ([emoji, search-name]) powering the Skool-style
// picker (search + category tabs + section labels). Dependency-free.
const CATEGORIES = [
  {
    key: "smileys",
    label: "Smileys & People",
    icon: "😀",
    list: [
      ["😀", "grinning"], ["😃", "smiley"], ["😄", "smile"], ["😁", "grin"], ["😆", "laughing"],
      ["😅", "sweat smile"], ["🤣", "rofl"], ["😂", "joy laugh"], ["🙂", "slight smile"], ["🙃", "upside down"],
      ["😉", "wink"], ["😊", "blush"], ["😇", "innocent angel"], ["🥰", "love hearts"], ["😍", "heart eyes"],
      ["🤩", "star struck"], ["😘", "kiss heart"], ["😗", "kissing"], ["😚", "kiss closed"], ["😙", "kiss smile"],
      ["😋", "yum tasty"], ["😛", "tongue"], ["😜", "wink tongue"], ["🤪", "zany goofy"], ["😝", "squint tongue"],
      ["🤑", "money mouth"], ["🤗", "hug"], ["🤭", "hand over mouth"], ["🤫", "shush quiet"], ["🤔", "thinking"],
      ["🤐", "zipper quiet"], ["😐", "neutral"], ["😑", "expressionless"], ["😶", "no mouth"], ["😏", "smirk"],
      ["😒", "unamused"], ["🙄", "roll eyes"], ["😬", "grimace"], ["😮‍💨", "exhale"], ["😴", "sleeping"],
      ["😌", "relieved"], ["😔", "pensive"], ["😪", "sleepy"], ["🤤", "drooling"], ["😷", "mask sick"],
      ["🤒", "thermometer sick"], ["🤕", "bandage hurt"], ["🤢", "nauseated"], ["🤮", "vomiting"], ["🥵", "hot"],
      ["🥶", "cold"], ["😵", "dizzy"], ["🤯", "mind blown"], ["🤠", "cowboy"], ["🥳", "party"],
      ["😎", "sunglasses cool"], ["🤓", "nerd"], ["🧐", "monocle"], ["😕", "confused"], ["😟", "worried"],
      ["🙁", "frown"], ["😮", "open mouth"], ["😯", "hushed"], ["😲", "astonished"], ["😳", "flushed"],
      ["🥺", "pleading"], ["😢", "cry"], ["😭", "sob"], ["😦", "anguished"], ["😨", "fearful"],
      ["😰", "anxious"], ["😥", "sad relieved"], ["😱", "scream"], ["😖", "confounded"], ["😣", "persevere"],
      ["😞", "disappointed"], ["😓", "sweat"], ["😩", "weary"], ["😫", "tired"], ["🥱", "yawn"],
      ["😤", "triumph"], ["😡", "rage angry"], ["😠", "angry"], ["🤬", "cursing"], ["😈", "devil"],
      ["💀", "skull"], ["💩", "poop"], ["🤡", "clown"], ["👻", "ghost"], ["👽", "alien"],
      ["🤖", "robot"], ["👋", "wave hi"], ["🤚", "raised hand"], ["✋", "hand stop"], ["👌", "ok"],
      ["🤏", "pinch"], ["✌️", "victory peace"], ["🤞", "fingers crossed"], ["🤟", "love you"], ["🤘", "rock"],
      ["👍", "thumbs up like"], ["👎", "thumbs down"], ["👊", "fist punch"], ["👏", "clap"], ["🙌", "raise hands"],
      ["🙏", "pray thanks"], ["💪", "muscle strong"], ["🫶", "heart hands"],
    ],
  },
  {
    key: "animals",
    label: "Animals & Nature",
    icon: "🐻",
    list: [
      ["🐶", "dog"], ["🐱", "cat"], ["🐭", "mouse"], ["🐹", "hamster"], ["🐰", "rabbit"],
      ["🦊", "fox"], ["🐻", "bear"], ["🐼", "panda"], ["🐨", "koala"], ["🐯", "tiger"],
      ["🦁", "lion"], ["🐮", "cow"], ["🐷", "pig"], ["🐸", "frog"], ["🐵", "monkey"],
      ["🐔", "chicken"], ["🐧", "penguin"], ["🐦", "bird"], ["🦆", "duck"], ["🦉", "owl"],
      ["🐴", "horse"], ["🦄", "unicorn"], ["🐝", "bee"], ["🐛", "bug"], ["🦋", "butterfly"],
      ["🐢", "turtle"], ["🐍", "snake"], ["🐙", "octopus"], ["🐳", "whale"], ["🐬", "dolphin"],
      ["🐟", "fish"], ["🐊", "crocodile"], ["🐘", "elephant"], ["🦒", "giraffe"], ["🐊", "croc"],
      ["🌳", "tree"], ["🌲", "evergreen"], ["🌵", "cactus"], ["🌸", "blossom"], ["🌺", "hibiscus"],
      ["🌻", "sunflower"], ["🌹", "rose"], ["🌷", "tulip"], ["🍀", "clover luck"], ["🍁", "maple leaf"],
      ["⭐", "star"], ["🌟", "glowing star"], ["🌈", "rainbow"], ["🔥", "fire"], ["💧", "water drop"],
    ],
  },
  {
    key: "food",
    label: "Food & Drink",
    icon: "🍔",
    list: [
      ["🍏", "green apple"], ["🍎", "apple"], ["🍐", "pear"], ["🍊", "orange"], ["🍋", "lemon"],
      ["🍌", "banana"], ["🍉", "watermelon"], ["🍇", "grapes"], ["🍓", "strawberry"], ["🫐", "blueberry"],
      ["🍒", "cherry"], ["🍑", "peach"], ["🥭", "mango"], ["🍍", "pineapple"], ["🥥", "coconut"],
      ["🥝", "kiwi"], ["🍅", "tomato"], ["🥑", "avocado"], ["🌽", "corn"], ["🥕", "carrot"],
      ["🍔", "burger"], ["🍟", "fries"], ["🍕", "pizza"], ["🌭", "hotdog"], ["🥪", "sandwich"],
      ["🌮", "taco"], ["🌯", "burrito"], ["🍜", "noodles ramen"], ["🍝", "pasta"], ["🍣", "sushi"],
      ["🍱", "bento"], ["🍚", "rice"], ["🍦", "ice cream"], ["🍩", "donut"], ["🍪", "cookie"],
      ["🎂", "cake birthday"], ["🍰", "cake"], ["🍫", "chocolate"], ["🍬", "candy"], ["🍿", "popcorn"],
      ["☕", "coffee"], ["🍵", "tea"], ["🥤", "soda drink"], ["🍺", "beer"], ["🍷", "wine"],
      ["🥂", "cheers"], ["🍸", "cocktail"], ["🧃", "juice"], ["🧋", "boba tea"], ["🍾", "champagne"],
    ],
  },
  {
    key: "activity",
    label: "Activities",
    icon: "⚽",
    list: [
      ["⚽", "soccer football"], ["🏀", "basketball"], ["🏈", "american football"], ["⚾", "baseball"], ["🎾", "tennis"],
      ["🏐", "volleyball"], ["🏉", "rugby"], ["🎱", "8ball pool"], ["🏓", "ping pong"], ["🏸", "badminton"],
      ["🥅", "goal"], ["🏒", "hockey"], ["🏏", "cricket"], ["⛳", "golf"], ["🏹", "archery"],
      ["🎣", "fishing"], ["🥊", "boxing"], ["🥋", "martial arts"], ["⛸️", "skating"], ["🛹", "skateboard"],
      ["🎿", "ski"], ["🏂", "snowboard"], ["🏆", "trophy"], ["🥇", "gold medal"], ["🥈", "silver medal"],
      ["🥉", "bronze medal"], ["🎖️", "medal"], ["🏅", "sports medal"], ["🎮", "game controller"], ["🎲", "dice"],
      ["🎯", "target dart"], ["🎳", "bowling"], ["🎤", "mic sing"], ["🎧", "headphones"], ["🎸", "guitar"],
      ["🎹", "piano"], ["🎺", "trumpet"], ["🥁", "drum"], ["🎨", "art paint"], ["🎬", "movie clapper"],
    ],
  },
  {
    key: "travel",
    label: "Travel & Places",
    icon: "✈️",
    list: [
      ["🚗", "car"], ["🚕", "taxi"], ["🚙", "suv"], ["🚌", "bus"], ["🏎️", "race car"],
      ["🚓", "police car"], ["🚑", "ambulance"], ["🚒", "fire truck"], ["🚜", "tractor"], ["🛵", "scooter"],
      ["🏍️", "motorcycle"], ["🚲", "bicycle"], ["✈️", "airplane"], ["🚀", "rocket"], ["🛸", "ufo"],
      ["🚁", "helicopter"], ["⛵", "sailboat"], ["🚤", "speedboat"], ["🚢", "ship"], ["🚂", "train"],
      ["🚆", "metro"], ["🗺️", "map"], ["🗽", "statue liberty"], ["🗼", "tower"], ["🏰", "castle"],
      ["🏟️", "stadium"], ["🏖️", "beach"], ["🏝️", "island"], ["⛰️", "mountain"], ["🌋", "volcano"],
      ["🏕️", "camping"], ["🏙️", "city"], ["🌃", "night city"], ["🌉", "bridge"], ["🎡", "ferris wheel"],
      ["🎢", "roller coaster"], ["🎆", "fireworks"], ["🌅", "sunrise"], ["🌄", "sunrise mountain"], ["🗿", "moai"],
    ],
  },
  {
    key: "objects",
    label: "Objects",
    icon: "💡",
    list: [
      ["⌚", "watch"], ["📱", "phone"], ["💻", "laptop"], ["🖥️", "desktop computer"], ["🖨️", "printer"],
      ["⌨️", "keyboard"], ["🖱️", "mouse"], ["💾", "floppy save"], ["💿", "cd"], ["📷", "camera"],
      ["📸", "camera flash"], ["🎥", "video camera"], ["📺", "tv"], ["📻", "radio"], ["☎️", "telephone"],
      ["🔋", "battery"], ["🔌", "plug"], ["💡", "bulb idea"], ["🔦", "flashlight"], ["🕯️", "candle"],
      ["📕", "book"], ["📚", "books"], ["📝", "memo note"], ["✏️", "pencil"], ["🖊️", "pen"],
      ["📌", "pin"], ["📎", "paperclip"], ["✂️", "scissors"], ["🔑", "key"], ["🔒", "lock"],
      ["🔓", "unlock"], ["🔔", "bell"], ["📣", "megaphone"], ["💰", "money bag"], ["💳", "credit card"],
      ["💎", "gem diamond"], ["⚙️", "gear"], ["🔧", "wrench"], ["🧰", "toolbox"], ["🛒", "cart"],
    ],
  },
  {
    key: "symbols",
    label: "Symbols",
    icon: "❤️",
    list: [
      ["❤️", "red heart love"], ["🧡", "orange heart"], ["💛", "yellow heart"], ["💚", "green heart"], ["💙", "blue heart"],
      ["💜", "purple heart"], ["🖤", "black heart"], ["🤍", "white heart"], ["🤎", "brown heart"], ["💔", "broken heart"],
      ["❣️", "heart exclamation"], ["💕", "two hearts"], ["💞", "revolving hearts"], ["💓", "beating heart"], ["💗", "growing heart"],
      ["💖", "sparkling heart"], ["💘", "heart arrow"], ["💝", "heart gift"], ["💟", "heart decoration"], ["💯", "hundred"],
      ["✨", "sparkles"], ["⭐", "star"], ["🌟", "glowing star"], ["💫", "dizzy star"], ["⚡", "zap lightning"],
      ["🔥", "fire lit"], ["💥", "boom"], ["✅", "check"], ["❌", "cross x"], ["❗", "exclamation"],
      ["❓", "question"], ["⚠️", "warning"], ["🚫", "no prohibited"], ["♻️", "recycle"], ["🔆", "bright"],
      ["🎵", "music note"], ["🎶", "notes"], ["➕", "plus"], ["➖", "minus"], ["✔️", "check mark"],
    ],
  },
  {
    key: "flags",
    label: "Flags",
    icon: "🏁",
    list: [
      ["🏁", "checkered flag"], ["🚩", "triangular flag"], ["🏳️", "white flag"], ["🏴", "black flag"], ["🏳️‍🌈", "rainbow pride flag"],
      ["🇺🇸", "usa flag"], ["🇬🇧", "uk flag"], ["🇨🇦", "canada flag"], ["🇮🇳", "india flag"], ["🇦🇺", "australia flag"],
      ["🇩🇪", "germany flag"], ["🇫🇷", "france flag"], ["🇯🇵", "japan flag"], ["🇧🇷", "brazil flag"], ["🇿🇦", "south africa flag"],
      ["🇸🇬", "singapore flag"],
    ],
  },
];

const RECENT_KEY = "emoji_recent_v1";
const readRecent = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
  } catch {
    return [];
  }
};

function EmojiBtn({ e, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-2xl leading-none rounded-lg p-1 hover:bg-gray-100 transition"
    >
      {e}
    </button>
  );
}

// Skool-style emoji picker: search bar, category tabs (scroll anchors), section
// labels, recently-used row, and a scrollable grid. Calls onPick(emoji).
export default function EmojiPicker({ onPick, className = "", dropUp = true, icon = null }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [recent, setRecent] = useState(readRecent);
  // On phones the panel becomes a full-width sheet next to the trigger, so it is
  // never a cramped 320px popover jammed against a screen edge. Null on desktop,
  // where the positioned popover is used instead.
  // { top?, bottom?, maxHeight } — one edge or the other, never both, so the
  // sheet can hang off whichever side of the trigger actually has the room, and
  // its height is clamped to that room so it cannot spill off the screen.
  const [sheet, setSheet] = useState(null);
  // Fixed coordinates for the desktop popover. It used to be `sm:absolute`
  // inside the trigger's own box, which is fine until the trigger lives in a
  // scrolling container — the Create Post modal's body, every comment thread —
  // where the panel is simply clipped at the container's edge. Portalling it to
  // <body> means the only thing that can crop it is the viewport.
  const [pos, setPos] = useState(null);
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const bodyRef = useRef(null);
  const secRefs = useRef({});

  const PANEL_W = 332;
  const MARGIN = 12;

  const place = useCallback(() => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    if (window.innerWidth < 640) {
      // Which side of the trigger the sheet hangs from is decided by the room,
      // not assumed. It used to always sit ABOVE — right for a comment box at
      // the foot of the screen, wrong for the Create Post composer, whose emoji
      // button is near the TOP: there the sheet ran off the top of the screen
      // and lost its search field and half its rows.
      const roomAbove = Math.round(rect.top - MARGIN);
      const roomBelow = Math.round(window.innerHeight - rect.bottom - MARGIN);
      // Half the viewport at most. Filling the room outright covered the
      // conversation AND the app header on a tall phone — no context left, and
      // nothing visible to tap to dismiss it.
      const cap = Math.round(window.innerHeight * 0.5);
      setSheet(
        roomAbove >= roomBelow
          ? {
              bottom: Math.round(window.innerHeight - rect.top + 8),
              maxHeight: Math.min(roomAbove - 8, cap),
            }
          : { top: Math.round(rect.bottom + 8), maxHeight: Math.min(roomBelow - 8, cap) },
      );
      setPos(null);
      return;
    }
    setSheet(null);
    const panelH = panelRef.current?.offsetHeight || 384;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const up = dropUp
      ? !(spaceAbove < panelH + MARGIN && spaceBelow > spaceAbove)
      : spaceBelow < panelH + MARGIN && spaceAbove > spaceBelow;
    // Right-aligned to the trigger, then clamped inside the viewport on both
    // axes so it can never hang off an edge.
    let left = rect.right - PANEL_W;
    left = Math.max(MARGIN, Math.min(left, window.innerWidth - PANEL_W - MARGIN));
    let top = up ? rect.top - panelH - 8 : rect.bottom + 8;
    top = Math.max(MARGIN, Math.min(top, window.innerHeight - panelH - MARGIN));
    setPos({ top, left });
  }, [dropUp]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    place();
    // Re-place on scroll (capture, so it also catches the modal body's own
    // scrolling) and on resize, or the panel detaches from its button.
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      // The panel is portalled, so it is NOT inside rootRef any more — without
      // checking it too, the first click on any emoji would close the picker
      // before it could register.
      if (rootRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    addOutsidePress(onDoc);
    return () => removeOutsidePress(onDoc);
  }, [open]);

  const pick = (e) => {
    onPick(e);
    setRecent((prev) => {
      const next = [e, ...prev.filter((x) => x !== e)].slice(0, 21);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const query = q.trim().toLowerCase();
  const results = useMemo(() => {
    if (!query) return null;
    const out = [];
    for (const c of CATEGORIES)
      for (const [e, n] of c.list) if (n.includes(query)) out.push(e);
    return out;
  }, [query]);

  const scrollTo = (key) => {
    const el = secRefs.current[key];
    if (el && bodyRef.current) bodyRef.current.scrollTo({ top: el.offsetTop, behavior: "smooth" });
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Add emoji"
        className="flex items-center justify-center text-gray-500 hover:text-gray-700 transition"
      >
        {icon || (
          <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
            <circle cx="12" cy="12" r="9" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 10h.01M15 10h.01M8.5 14.5a4 4 0 007 0" />
          </svg>
        )}
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={
              sheet
                ? {
                    position: "fixed",
                    left: 0,
                    right: 0,
                    ...(sheet.top != null ? { top: sheet.top } : { bottom: sheet.bottom }),
                    maxHeight: sheet.maxHeight,
                  }
                : // Hidden off-screen for the first paint, before `place()` has
                  // measured — otherwise the panel flashes at the top-left corner.
                  { position: "fixed", top: pos?.top ?? -9999, left: pos?.left ?? -9999, width: PANEL_W }
            }
            /* Above the composer modal (z-50), the comments sheet (9999) and its
               action sheet (10001) — a picker that opens behind the thing that
               opened it is no picker at all. */
            /* The rounded edge goes on the side facing away from the trigger,
               so the sheet reads as sliding out of it either way. */
            className={`z-[10050] flex flex-col overflow-hidden bg-surface shadow-2xl ${
              sheet
                ? sheet.top != null
                  ? "rounded-b-2xl border-b border-gray-200"
                  : "rounded-t-2xl border-t border-gray-200"
                : "rounded-2xl border border-gray-200"
            }`}
          >
          {/* Search */}
          <div className="p-2.5 border-b border-gray-100">
            <div className="relative">
              <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.3-4.3M11 18a7 7 0 100-14 7 7 0 000 14z" />
              </svg>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search"
                className="w-full text-sm bg-gray-100 rounded-lg pl-9 pr-3 py-2 outline-none focus:bg-gray-50 placeholder:text-gray-400"
              />
            </div>
          </div>

          {/* Category tabs (hidden while searching) */}
          {!query && (
            <div className="flex items-center px-1.5 py-1 border-b border-gray-100">
              {recent.length > 0 && (
                <button
                  type="button"
                  title="Recently used"
                  onClick={() => scrollTo("recent")}
                  className="flex-1 flex items-center justify-center rounded-md py-1.5 text-gray-400 hover:bg-gray-100"
                >
                  <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.8">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 2m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </button>
              )}
              {CATEGORIES.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  title={c.label}
                  onClick={() => scrollTo(c.key)}
                  className="flex-1 text-lg leading-none rounded-md py-1 hover:bg-gray-100"
                >
                  {c.icon}
                </button>
              ))}
            </div>
          )}

          {/* Body */}
          <div ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto p-2 scrollbar-hide sm:max-h-64 sm:flex-none">
            {query ? (
              results.length > 0 ? (
                <>
                  <p className="px-1 py-1 text-xs font-semibold text-gray-400">Search Results</p>
                  <div className="grid grid-cols-7 gap-0.5">
                    {results.map((e, i) => (
                      <EmojiBtn key={i} e={e} onClick={() => pick(e)} />
                    ))}
                  </div>
                </>
              ) : (
                <p className="py-10 text-center text-sm text-gray-400">No emoji found</p>
              )
            ) : (
              <>
                {recent.length > 0 && (
                  <div ref={(el) => (secRefs.current.recent = el)}>
                    <p className="px-1 py-1 text-xs font-semibold text-gray-400">Recently used</p>
                    <div className="grid grid-cols-7 gap-0.5">
                      {recent.map((e, i) => (
                        <EmojiBtn key={i} e={e} onClick={() => pick(e)} />
                      ))}
                    </div>
                  </div>
                )}
                {CATEGORIES.map((c) => (
                  <div key={c.key} ref={(el) => (secRefs.current[c.key] = el)}>
                    <p className="px-1 py-1 mt-1 text-xs font-semibold text-gray-400">{c.label}</p>
                    <div className="grid grid-cols-7 gap-0.5">
                      {c.list.map(([e], i) => (
                        <EmojiBtn key={i} e={e} onClick={() => pick(e)} />
                      ))}
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
